import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import AgentConfigModal from '../../components/agentapps/AgentConfigModal';
import AgentService from '../../services/api/agents';
import moment from 'moment';
import { Button } from 'react-bootstrap';
import { default_size, providerLogo as PROVIDER_LOGO } from '../../settings/shared/constants';
import toast from 'react-hot-toast';
import SAModalConfirm from '../../components/common/SAModalConfirm';
import { FaCog, FaRegCalendarAlt, FaCheck, FaEye, FaEdit } from 'react-icons/fa';
import AGENT_APPS_DATA from './Agent_apps_data';
import SAPagination from '../../components/common/SAPagination';

// ============================================================================
// HELPERS
// ============================================================================

const OAUTH_PROVIDERS = ['slack', 'linkedin', 'salesforce', 'microsoft-teams'];

// "OPEN_API" / "open-api" / "openapi" -> "openapi"
const normalizeProvider = (v) => String(v || '').toLowerCase().replace(/[_\-\s]/g, '');

// Agent id -> provider key used by the "get connected apps by provider" API
const toProviderApiKey = (agentId) => {
  const id = String(agentId || '');
  if (id.includes('google')) return 'GOOGLE';
  if (id.toLowerCase() === 'open_api') return 'OPEN_API';
  return id.toUpperCase();
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

/**
 * AgentCard - Memoized card component for individual agent
 */
const AgentCard = React.memo(({ agent, onConnect, onViewSpecificAppsConnected, onConfigure }) => {
  const handleConnect = useCallback((e) => {
    e.preventDefault();
    if (onConnect && agent.status === 'active') {
      onConnect(agent.id, agent.name);
    }
  }, [agent.id, agent.name, agent.status, onConnect]);

  const handleViewSpecificAppsConnected = useCallback((e) => {
    e.preventDefault();
    if (onViewSpecificAppsConnected) {
      onViewSpecificAppsConnected(agent.id, agent.name);
    }
  }, [agent.id, agent.name, onViewSpecificAppsConnected]);

  const handleConfigure = useCallback((e) => {
    e.preventDefault();
    if (onConfigure && agent.status === 'active') {
      onConfigure(agent.id, agent.name);
    }
  }, [agent.id, agent.name, agent.status, onConfigure]);

  const handleKeyPress = useCallback((e, handler) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handler(e);
    }
  }, []);

  return (
      <div className="agent-card" role="article" aria-label={`${agent.name} integration`}>
        <div className="agent-card-header">
          <img
              src={agent.icon}
              alt=""
              aria-hidden="true"
              loading="lazy"
              width="36"
              height="36"
              onError={(e) => {
                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="36" height="36"%3E%3Crect fill="%23ddd" width="36" height="36"/%3E%3C/svg%3E';
              }}
          />
          <h3>{agent.name}</h3>
        </div>
        <p>{agent.description}</p>

        {agent.status === 'coming-soon' ? (
            <button
                className="coming-soon"
                disabled
                aria-label={`${agent.name} integration - Coming soon`}
                type="button"
            >
              Coming soon
            </button>
        ) : (
            <div className="actions" role="group" aria-label={`${agent.name} actions`}>
              {agent.actions.includes('configure') && (
                  <button
                      className="connect"
                      onClick={handleConfigure}
                      onKeyPress={(e) => handleKeyPress(e, handleConfigure)}
                      aria-label={`Configure ${agent.name}`}
                      type="button"
                  >
                    <FaCog style={{ marginRight: '6px' }} />
                    Configure
                  </button>
              )}
              {agent.actions.includes('connect') && (
                  <button
                      className="connect"
                      onClick={handleConnect}
                      onKeyPress={(e) => handleKeyPress(e, handleConnect)}
                      aria-label={`Connect to ${agent.name}`}
                      type="button"
                  >
                    Connect
                  </button>
              )}
              {agent.actions.includes('view-triggers') && (
                  <button
                      className="view"
                      onClick={handleViewSpecificAppsConnected}
                      onKeyPress={(e) => handleKeyPress(e, handleViewSpecificAppsConnected)}
                      aria-label="View"
                      type="button"
                  >
                    <FaEye style={{ marginRight: '6px' }} />
                    View
                  </button>
              )}
            </div>
        )}
      </div>
  );
});

AgentCard.propTypes = {
  agent: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    status: PropTypes.oneOf(['active', 'coming-soon']).isRequired,
    actions: PropTypes.arrayOf(PropTypes.string).isRequired,
    provider: PropTypes.string.isRequired
  }).isRequired,
  onConnect: PropTypes.func,
  onViewSpecificAppsConnected: PropTypes.func,
  onConfigure: PropTypes.func
};

AgentCard.displayName = 'AgentCard';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * AgentApps - Main component displaying available agent integrations
 */
const AgentApps = () => {
  const agentApps = useMemo(() => AGENT_APPS_DATA, []);
  const itemsScrollRef = useRef(null);

  // Group apps by provider/category
  const appsByCategory = useMemo(() => {
    const grouped = {};
    agentApps.forEach((app) => {
      const category = app.category;
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(app);
    });
    return grouped;
  }, [agentApps]);

  // Get unique categories
  const categories = useMemo(() => {
    const categoryOrder = [
      'email-communication',
      'file-storage-collaboration',
      'spreadsheets-docs',
      'databases',
      'cloud-infrastructure',
      'devops-cicd',
      'crm-sales',
      'project-management',
      'marketing-social',
      'ecommerce-payments',
      'messaging-streaming',
      'analytics-monitoring',
      'calendar-tasks',
      'logistics-utilities',
      'auth',
      'ai',
      'observability',
      'api-integrations'
    ];
    const allCategories = Object.keys(appsByCategory);
    const ordered = categoryOrder.filter(cat => allCategories.includes(cat));
    const rest = allCategories.filter(cat => !categoryOrder.includes(cat)).sort();
    return [...ordered, ...rest];
  }, [appsByCategory]);

  // State for selected category
  const [selectedCategory, setSelectedCategory] = useState(categories[0] || '');

  // State for configuration wizard
  const [configuringAgent, setConfiguringAgent] = useState(null); // { id, name } or null
  const [editingConnectedApp, setEditingConnectedApp] = useState(null);

  // State for connected apps view
  const [showConnectedAppsModal, setShowConnectedAppsModal] = useState(false);
  const [connectedApps, setConnectedApps] = useState([]);
  const [loadingConnectedApps, setLoadingConnectedApps] = useState(false);
  const [connectedAppsError, setConnectedAppsError] = useState(null);
  // Provider whose "View" list is open (null = all connected apps)
  const [connectedAppsProvider, setConnectedAppsProvider] = useState(null);

  // State for delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState('');
  const [deleteAppName, setDeleteAppName] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  // Deep-link support (PropertiesPanel "+": open API Integrations → Rest API → Configure)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search || '');
      const action = (params.get('action') || '').toLowerCase();
      const provider = (params.get('provider') || '').toLowerCase();
      if (action !== 'configure') return;
      if (!provider) return;

      setShowConnectedAppsModal(false);
      setConnectedAppsError(null);

      if (provider === 'open_api' || provider === 'openapi' || provider === 'restapi') {
        setSearchQuery('');
        setSelectedCategory('api-integrations');

        const agentData = agentApps.find((a) => (a?.id || '').toLowerCase() === 'open_api');
        setConfiguringAgent({
          id: 'open_api',
          name: agentData?.name || 'Rest API'
        });
      }
    } catch (e) {
      // ignore deep-link errors
    }
    // Only run once per mount (agentApps is memoized)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Format count for badge display (e.g., 5000 -> "5k")
   */
  const formatCount = useCallback((count) => {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1).replace(/\.0$/, '')}k`;
    }
    return count.toString();
  }, []);

  // Get display name for category
  const getCategoryDisplayName = useCallback((category) => {
    const names = {
      'email-communication': 'Email & Communication',
      'file-storage-collaboration': 'File Storage & Collaboration',
      'spreadsheets-docs': 'Spreadsheets & Docs',
      'databases': 'Databases',
      'cloud-infrastructure': 'Cloud & Infrastructure',
      'devops-cicd': 'DevOps & CI/CD',
      'crm-sales': 'CRM & Sales',
      'project-management': 'Project Management',
      'marketing-social': 'Marketing & Social',
      'ecommerce-payments': 'E-commerce & Payments',
      'messaging-streaming': 'Messaging & Streaming',
      'analytics-monitoring': 'Analytics & Monitoring',
      'calendar-tasks': 'Calendar & Tasks',
      'logistics-utilities': 'Logistics & Utilities',
      'auth': 'Auth',
      'ai': 'AI',
      'observability': 'Observability',
      'api-integrations': 'API Integrations',
      'google': 'Google',
      'microsoft': 'Microsoft',
      'hubspot': 'HubSpot',
      'linear': 'Linear'
    };
    return names[category] || category.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  }, []);

  /**
   * Handle agent connection - opens configuration wizard
   */
  const handleConnect = useCallback((agentId, agentName) => {
    try {
      setSearchQuery('');
      const agent = agentApps.find((a) => a?.id === agentId);
      if (agent?.category && agent.category !== selectedCategory) {
        setSelectedCategory(agent.category);
      }
      setConfiguringAgent({ id: agentId, name: agentName });
    } catch (error) {
      console.error(`Error opening configuration for ${agentName}:`, error);
    }
  }, [agentApps, selectedCategory]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  /**
   * Load ALL connected apps for the user (paginated)
   */
  const loadConnectedApps = useCallback(async () => {
    setLoadingConnectedApps(true);
    setConnectedAppsError(null);

    try {
      const response = await AgentService.getConnectedAppsListByUser(currentPage);
      const appsData = response?.thirdPartyAppsDTOPage?.content || [];
      setTotalPages(response?.thirdPartyAppsDTOPage?.totalPages || 0);
      setTotalElements(response?.thirdPartyAppsDTOPage?.totalElements || 0);
      if (appsData.length > 0) {
        setConnectedApps(appsData);
      } else if (response?.status === 'SUCCESS' && appsData.length === 0) {
        setConnectedApps([]);
      } else {
        setConnectedAppsError(response?.errorMessage || 'Failed to load connected apps');
        setConnectedApps([]);
      }
    } catch (error) {
      console.error('Error fetching connected apps:', error);
      setConnectedAppsError(error?.errorMessage || 'Failed to load connected apps');
      setConnectedApps([]);
    } finally {
      setLoadingConnectedApps(false);
    }
  }, [currentPage]);

  /**
   * Load connected apps for ONE provider (used by the card "View" button and
   * to return to that provider's list after an edit)
   */
  const loadProviderApps = useCallback(async (providerKey) => {
    setLoadingConnectedApps(true);
    setConnectedAppsError(null);

    try {
      const appsresponse = await AgentService.getConnectedApps(providerKey);
      const appsData = appsresponse?.thirdPartyAppsDTOPage?.content || [];

      if (appsData.length > 0) {
        setConnectedApps(appsData);
      } else if (appsresponse?.status === 'SUCCESS' && appsData.length === 0) {
        setConnectedApps([]);
      } else {
        setConnectedAppsError(appsresponse?.errorMessage || 'Failed to load connected apps');
        setConnectedApps([]);
      }
    } catch (error) {
      console.error(`Error loading connected apps for ${providerKey}:`, error);
      setConnectedAppsError(error?.errorMessage || error?.message || 'Failed to load connected apps');
      setConnectedApps([]);
    } finally {
      setLoadingConnectedApps(false);
    }
  }, []);

  /**
   * Handle Connected Apps button click - shows ALL connected apps
   */
  const showConnectedApps = useCallback(async () => {
    setConfiguringAgent(null);
    setShowConnectedAppsModal(true);
    const wasFiltered = Boolean(connectedAppsProvider);
    setConnectedAppsProvider(null);
    if (
        wasFiltered ||
        (connectedApps.length === 0 && !loadingConnectedApps && !connectedAppsError)
    ) {
      await loadConnectedApps();
    }
  }, [connectedAppsProvider, connectedApps.length, loadingConnectedApps, connectedAppsError, loadConnectedApps]);

  useEffect(() => {
    const handleMessage = async (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type === 'GOOGLE_OAUTH_SUCCESS') {
        showConnectedApps();
      }
    };
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [showConnectedApps]);

  /**
   * Card "View" button - shows connected apps of that provider only
   */
  const handleViewSpecificAppsConnected = useCallback(async (agentId) => {
    setConfiguringAgent(null);
    setShowConnectedAppsModal(true);
    const providerKey = toProviderApiKey(agentId);
    setConnectedAppsProvider(providerKey);
    await loadProviderApps(providerKey);
  }, [loadProviderApps]);

  const handleConfigure = useCallback((agentId, agentName) => {
    try {
      setSearchQuery('');
      const agent = agentApps.find((a) => a?.id === agentId);
      if (agent?.category && agent.category !== selectedCategory) {
        setSelectedCategory(agent.category);
      }
      setConfiguringAgent({ id: agentId, name: agentName });
    } catch (error) {
      console.error(`Error opening configuration for ${agentName}:`, error);
    }
  }, [agentApps, selectedCategory]);

  /**
   * Connected-app row -> its agent definition (the one holding configSchema)
   */
  const getAgentForConnectedApp = useCallback((app) => {
    const p = normalizeProvider(app?.provider);
    if (!p) return null;
    return (
        agentApps.find((a) => normalizeProvider(a?.provider) === p) ||
        agentApps.find((a) => normalizeProvider(a?.id) === p) ||
        null
    );
  }, [agentApps]);

  /**
   * Edit is available for every provider that has a configSchema and uses
   * manual (non-OAuth) config. OAuth providers are re-authorized instead.
   */
  const isEditableConnectedApp = useCallback((app) => {
    const agent = getAgentForConnectedApp(app);
    if (!agent?.configSchema) return false;
    const p = String(app?.provider || '').toLowerCase();
    const isOAuth =
        Boolean(agent.configSchema.oauth) ||
        OAUTH_PROVIDERS.includes(p) ||
        p.includes('google');
    return !isOAuth;
  }, [getAgentForConnectedApp]);

  const handleEditConnectedApp = useCallback((app) => {
    const agentData = getAgentForConnectedApp(app);

    if (!agentData?.configSchema) {
      toast.error('Edit is not available for this provider yet.');
      return;
    }

    setSearchQuery('');
    setEditingConnectedApp(app);
    setShowConnectedAppsModal(false);
    setConfiguringAgent({ id: agentData.id, name: agentData.name });
  }, [getAgentForConnectedApp]);

  /**
   * Back / Close from the config screen. After an edit, return to the
   * connected apps list (same provider list the user came from).
   */
  const handleCloseConfig = useCallback(() => {
    const wasEditing = Boolean(editingConnectedApp);
    setConfiguringAgent(null);
    setEditingConnectedApp(null);
    if (wasEditing) {
      setShowConnectedAppsModal(true);
    }
  }, [editingConnectedApp]);

  /**
   * Handle configuration save
   */
  const handleConfigSave = useCallback(async () => {
    try {
      const wasEdit = Boolean(editingConnectedApp?.id);
      setConfiguringAgent(null);
      setEditingConnectedApp(null);
      setShowConnectedAppsModal(true);
      toast.success(wasEdit ? 'Connected app updated' : 'Configuration saved');

      if (wasEdit && connectedAppsProvider) {
        // Edited from a provider's "View" list -> stay on that provider's list
        await loadProviderApps(connectedAppsProvider);
      } else {
        setConnectedAppsProvider(null);
        await loadConnectedApps();
      }

      requestAnimationFrame(() => {
        itemsScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      });
    } catch (error) {
      console.error('Error saving configuration:', error);
    }
  }, [loadConnectedApps, loadProviderApps, editingConnectedApp, connectedAppsProvider]);

  /**
   * Load connected apps when component mounts / page changes
   */
  useEffect(() => {
    loadConnectedApps();
  }, [loadConnectedApps]);

  /**
   * Handle Close button click - closes connected apps view
   */
  const handleClose = useCallback(() => {
    setShowConnectedAppsModal(false);
    setConnectedAppsError(null);
    setConnectedAppsProvider(null);
  }, []);

  /**
   * Handle category selection - closes connected apps modal and shows category apps
   */
  const handleCategorySelect = useCallback((category) => {
    setConfiguringAgent(null);
    setEditingConnectedApp(null);
    setShowConnectedAppsModal(false);
    setConnectedAppsProvider(null);
    setSelectedCategory(category);
  }, []);

  const getProviderLogo = useCallback((provider) => {
    if (!provider) return null;

    const providerLower = provider.toLowerCase();

    if (PROVIDER_LOGO[providerLower]) {
      return PROVIDER_LOGO[providerLower];
    }

    for (const [key, value] of Object.entries(PROVIDER_LOGO)) {
      if (providerLower.includes(key) || key.includes(providerLower)) {
        return value;
      }
    }
    return null;
  }, []);

  // Get apps for selected category
  const selectedCategoryApps = useMemo(() => {
    return appsByCategory[selectedCategory] || [];
  }, [selectedCategory, appsByCategory]);

  const filteredCategoryApps = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase();
    if (!q) return selectedCategoryApps;
    return (selectedCategoryApps || []).filter((a) => {
      const name = (a?.name || '').toLowerCase();
      const desc = (a?.description || '').toLowerCase();
      return name.includes(q) || desc.includes(q);
    });
  }, [selectedCategoryApps, searchQuery]);

  const filteredAllApps = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase();
    if (!q) return agentApps;
    return (agentApps || []).filter((a) => {
      const name = (a?.name || '').toLowerCase();
      const desc = (a?.description || '').toLowerCase();
      const category = (a?.category || '').toLowerCase();
      return name.includes(q) || desc.includes(q) || category.includes(q);
    });
  }, [agentApps, searchQuery]);

  const appsToRender = useMemo(() => {
    const q = (searchQuery || '').trim();
    return q ? filteredAllApps : filteredCategoryApps;
  }, [filteredAllApps, filteredCategoryApps, searchQuery]);

  const filteredConnectedApps = useMemo(() => {
    const q = (searchQuery || '').trim().toLowerCase();
    if (!q) return connectedApps;
    return (connectedApps || []).filter((a) => {
      const name = (a?.name || '').toLowerCase();
      const provider = (a?.provider || '').toLowerCase();
      return name.includes(q) || provider.includes(q);
    });
  }, [connectedApps, searchQuery]);

  const showDeleteModalHandler = useCallback((event, app) => {
    event.preventDefault();
    setDeleteId(app?.id || '');
    setDeleteAppName(app?.name || '');
    setShowDeleteModal(true);
  }, []);

  /**
   * Handle delete confirmation - performs actual deletion
   */
  const handleDeleteOk = useCallback(async () => {
    if (!deleteId) return;

    try {
      const response = await AgentService.deleteOAuth(deleteId);

      if (response?.status === 'SUCCESS') {
        toast.success('Connected app deleted successfully');
        setConnectedApps(prevApps => prevApps.filter((app) => app.id !== deleteId));
        setShowDeleteModal(false);
        setDeleteId('');
        setDeleteAppName('');
      } else {
        toast.error(response?.errorMessage || 'Failed to delete connected app');
      }
    } catch (error) {
      console.error('Error deleting connected app:', error);
      toast.error(error?.errorMessage || 'Failed to delete connected app');
    }
  }, [deleteId]);

  /**
   * Handle delete cancellation - closes modal without deleting
   */
  const handleDeleteCancel = useCallback(() => {
    setShowDeleteModal(false);
    setDeleteId('');
    setDeleteAppName('');
  }, []);

  return (
      <div className="agent-apps-layout" role="main" aria-label="Agent Applications">
        {/* Left Sidebar - Categories */}
        <div className="agent-categories-sidebar">
          <button
              className="connected-apps-sidebar-button"
              onClick={showConnectedApps}
              aria-label="View Connected Apps"
              type="button"
          >
            <span className="connected-apps-button-text">Connected Apps</span>
            <span className="connected-apps-badge">{totalElements > 0 ? formatCount(totalElements) : '0'}</span>
          </button>
          <hr></hr>
          <div className="categories-scroll">
            <nav className="categories-menu" role="navigation" aria-label="Agent app categories">
              {categories.map((category) => (
                  <button
                      key={category}
                      className={`category-item ${selectedCategory === category ? 'active' : ''}`}
                      onClick={() => handleCategorySelect(category)}
                      aria-label={`View ${getCategoryDisplayName(category)} apps`}
                      aria-pressed={selectedCategory === category}
                      type="button"
                  >
                    {getCategoryDisplayName(category)}
                    <span className="category-count">({appsByCategory[category].length})</span>
                  </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Right Content Area - Items or Configuration Wizard */}
        <div className="agent-items-content">
          {/* Header with search and back button */}
          <div className="agent-apps-header">
            <h2 className="items-title">
              {showConnectedAppsModal
                  ? 'Connected Apps'
                  : ((searchQuery || '').trim()
                      ? 'Search results'
                      : `${getCategoryDisplayName(selectedCategory)} Apps`)}
            </h2>
            <div className="agent-apps-header-actions">
              {!configuringAgent && (
                  <div className="agent-apps-search">
                    <i className="fa-solid fa-magnifying-glass agent-apps-search-icon" />
                    <input
                        className="agent-apps-search-input"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={showConnectedAppsModal ? 'Search connected apps…' : 'Search apps…'}
                    />
                    {searchQuery ? (
                        <button
                            type="button"
                            className="agent-apps-search-clear"
                            onClick={() => setSearchQuery('')}
                            title="Clear"
                        >
                          <i className="fa-solid fa-xmark" />
                        </button>
                    ) : null}
                  </div>
              )}

              {showConnectedAppsModal && (
                  <button
                      className="back-button"
                      onClick={handleClose}
                      aria-label="Back to Categories"
                      type="button"
                  >
                    <i className="fa fa-long-arrow-left"></i>
                  </button>
              )}
            </div>
          </div>
          <div className="agent-items-scroll" ref={itemsScrollRef}>
            {configuringAgent ? (
                (() => {
                  const agentData = agentApps.find(app => app.id === configuringAgent.id);

                  if (agentData && agentData.configSchema) {
                    return (
                        <AgentConfigModal
                            provider={configuringAgent.id}
                            agentName={configuringAgent.name}
                            configSchema={agentData.configSchema}
                            editRecord={editingConnectedApp}
                            onClose={handleCloseConfig}
                            onSave={handleConfigSave}
                        />
                    );
                  }

                  return (
                      <div className="agent-apps-config-fallback">
                        <p>Configuration wizard for {configuringAgent.name} coming soon</p>
                        <button type="button" onClick={handleCloseConfig}>Go Back</button>
                      </div>
                  );
                })()
            ) : showConnectedAppsModal ? (
                /* Connected Apps Table View */
                <div className="connected-apps-content">
                  {loadingConnectedApps ? (
                      <div className="connected-apps-loading">
                        <div className="connected-apps-spinner"></div>
                        <p>Loading connected apps...</p>
                      </div>
                  ) : connectedAppsError === 'COMING_SOON' ? (
                      <div className="connected-apps-coming-soon">
                        <p className="connected-apps-coming-soon-title">Coming Soon</p>
                        <p className="connected-apps-coming-soon-desc">This integration will be available shortly.</p>
                      </div>
                  ) : connectedAppsError ? (
                      <div className="connected-apps-error">
                        <p className="connected-apps-error-message">{connectedAppsError}</p>
                        <button
                            type="button"
                            className="connected-apps-retry-btn"
                            onClick={() =>
                                connectedAppsProvider
                                    ? loadProviderApps(connectedAppsProvider)
                                    : loadConnectedApps()
                            }
                        >
                          Retry
                        </button>
                      </div>
                  ) : connectedApps.length === 0 ? (
                      <div className="connected-apps-empty">
                        <p className="connected-apps-empty-message">No connected apps found.</p>
                      </div>
                  ) : (
                      <div className="connected-apps-table-wrapper">
                        <table className="table table-hover mb-0">
                          <thead className="table-light">
                          <tr>
                            <th className="text-start fw-bold py-3 px-3">Name</th>
                            <th className="text-start fw-bold py-3 px-3">Provider</th>
                            <th className="text-start fw-bold py-3 px-3">Status</th>
                            <th className="text-start fw-bold py-3 px-3">Created Date</th>
                            <th className="text-start fw-bold py-3 px-3">Actions</th>
                          </tr>
                          </thead>
                          <tbody>
                          {filteredConnectedApps.length > 0 ? (
                              filteredConnectedApps.map((app) => {
                                const providerLogo = getProviderLogo(app.provider);
                                const canEdit = isEditableConnectedApp(app);
                                return (
                                    <tr key={app.id}>
                                      <td className="text-start py-3 px-3">
                                        <div className="d-flex align-items-center">
                                          <span>{app.name}</span>
                                        </div>
                                      </td>
                                      <td className="text-start py-3 px-3">
                                        <div className="d-flex align-items-center gap-2">
                                          {providerLogo && (
                                              <img
                                                  src={providerLogo}
                                                  alt={app.provider}
                                                  className="connected-apps-provider-logo"
                                                  onError={(e) => {
                                                    e.target.style.display = 'none';
                                                  }}
                                              />
                                          )}
                                          <span className="connected-apps-provider-name">
                                    {app.provider}
                                  </span>
                                        </div>
                                      </td>
                                      <td className="text-start py-3 px-3">
                                <span className="badge bg-success font-weight-bold font12">
                                  <FaCheck /> Connected
                                </span>
                                      </td>
                                      <td className="text-start py-3 px-3">
                                        <FaRegCalendarAlt style={{ marginRight: '6px' }} />
                                        <span className="created-date-text">{moment(app.createdDate).format('MMM DD, YYYY')}</span>
                                      </td>
                                      <td className="text-end py-3 px-3">
                                        <div className="d-flex gap-2 justify-content-end">
                                          {canEdit ? (
                                              <Button
                                                  variant="link"
                                                  size="sm"
                                                  title="Edit"
                                                  className="text-primary p-1 border-0 shadow-none"
                                                  onClick={() => handleEditConnectedApp(app)}
                                              >
                                                <FaEdit />
                                              </Button>
                                          ) : null}
                                          <Button
                                              variant="outline-danger"
                                              size="sm"
                                              title="Delete"
                                              onClick={(e) => showDeleteModalHandler(e, app)}
                                          >
                                            <i className="fas fa-trash"></i>
                                          </Button>
                                        </div>
                                      </td>
                                    </tr>
                                );
                              })
                          ) : (
                              <tr>
                                <td colSpan="5" className="text-center py-4 text-muted">
                                  No connected apps found
                                </td>
                              </tr>
                          )}
                          </tbody>
                        </table>

                        {!connectedAppsProvider && (
                            <SAPagination
                                currentPage={currentPage}
                                totalPages={totalPages || 0}
                                totalItems={totalElements || 0}
                                itemsPerPage={default_size}
                                showPageInfo={true}
                                showGoToPage={true}
                                onPageChange={handlePageChange}
                            />
                        )}
                      </div>
                  )}
                </div>
            ) : (
                /* Agent Cards View */
                <div className="agent-container">
                  {appsToRender.length > 0 ? (
                      appsToRender.map((agent) => (
                          <AgentCard
                              key={agent.id}
                              agent={agent}
                              onConnect={handleConnect}
                              onViewSpecificAppsConnected={handleViewSpecificAppsConnected}
                              onConfigure={handleConfigure}
                          />
                      ))
                  ) : (
                      <div className="no-items-message">
                        {searchQuery ? 'No apps match your search.' : 'No apps available in this category.'}
                      </div>
                  )}
                </div>
            )}
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        <SAModalConfirm
            show={showDeleteModal}
            title="Delete Connected App"
            message={`Are you sure you want to delete ${deleteAppName}?`}
            okText="Delete"
            cancelText="Cancel"
            handleOk={handleDeleteOk}
            handleCancel={handleDeleteCancel}
            zIndex={2100}
        />
      </div>
  );
};

AgentApps.displayName = 'AgentApps';

export default AgentApps;